import { FC, useState, useMemo } from 'react';
import {
  Breakpoints,
  ButtonSize,
  QuantityStepperSize,
  useBreakpoint,
  QuantityStepper,
} from '@nextgen-web-framework/all';
import { PlanResponse } from '@commerce-subscription-webcomponents/sdk';
import SubscribeButton from '../SubscribeButton/SubscribeButton';
import { DataFlowEnum } from '../SubscribeButton/types/propTypes';
import { useSubscriptionQuantityContext } from '../../subscription-plan/molecules';
import { TemplateEnum } from '../../subscription-plan/type/propTypes';
import { useSubscriptionsSelector } from '../../../utils/utils';
import { SubscribeStepperPropTypes } from './types/propTypes';
import styles from './SubscribeStepperComponent.module.scss';

/**
 *
 * @param props
 * @returns
 */
const SubscribeStepperComponent: FC<SubscribeStepperPropTypes> = ({
  productId,
  productType,
  dataFlow,
  onSuccess,
  disabled,
  productList,
  isBasketSizePlan,
  bundleSlots,
}) => {
  const viewPort = useBreakpoint();
  const isMobile = viewPort === Breakpoints.XS || viewPort === Breakpoints.SM;
  const quantityContext = useSubscriptionQuantityContext();
  const [disabledBtn, setDisabledBtn] = useState<boolean>(false);
  const [count, setCount] = useState(1);
  const { bundleType, enableSubscription, selectedSubscriptionPlan } = useSubscriptionsSelector(
    (state) => state.commerceSubscription,
  );

  const changeHandler = (n: number): 0 => {
    quantityContext?.setQuantity(n);
    setCount(n);
    setDisabledBtn(n < 1);
    return 0;
  };

  // derive min and max quantity for the stepper component
  const [minQuantity, maxQuantity] = useMemo(() => {
    let [min, max] = [1, 1];
    const plan = selectedSubscriptionPlan?.plan as PlanResponse;

    const product = plan?.productOptions?.find((option) => [option.itemNumber, option.productId].includes(productId));

    if (product?.minQuantity && product?.maxQuantity) {
      min = product.minQuantity;
      max = product.maxQuantity;
    }
    setCount(min);
    return [min, max];
  }, [selectedSubscriptionPlan]);

  const checkForDisable =
    bundleType === TemplateEnum.DYNAMIC ? (disabled ? disabled : !enableSubscription) : disabledBtn || disabled;

  return (
    <div className={styles['subscribe-stepper-container']} data-testid="subscribe-stepper-testid">
      {maxQuantity > 1 && (
        <div className={styles['stepper-btn']}>
          <QuantityStepper
            count={quantityContext?.quantity ?? count}
            inputChangeHandler={changeHandler}
            minusClickHandler={changeHandler}
            plusClickHandler={changeHandler}
            disabled={disabled}
            minCount={minQuantity}
            maxCount={maxQuantity}
            size={isMobile ? QuantityStepperSize.SMALL : QuantityStepperSize.MEDIUM}
            className={styles['stepper-input']}
          />
        </div>
      )}
      <SubscribeButton
        productId={productId}
        quantity={quantityContext?.quantity ?? count}
        productType={productType}
        dataFlow={dataFlow as DataFlowEnum}
        disabled={checkForDisable}
        size={ButtonSize.MEDIUM}
        bundleSlots={bundleSlots}
        onSuccess={onSuccess}
        productList={productList}
        isBasketSizePlan={isBasketSizePlan}
      />
    </div>
  );
};

export default SubscribeStepperComponent;
